import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Dropzone from "react-dropzone";
import { Loader2, UploadCloud } from "lucide-react";
import { useFormContext } from "context/BlogContext";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

interface BlogInfoProps {
  handleNext: () => void;
}

const BlogInfoForm: React.FC<BlogInfoProps> = ({ handleNext }) => {
  const {
    blogValidation,
    setBlogInfo,
    setBlogMedia,
    blogInfo,
    isBlogEditing,
    handlePublishBlog,
  } = useFormContext();
  const [selectedCoverImage, setSelectedCoverImage] = useState<any>({});
  const [categoryInputValue, setCategoryInputValue] = useState<string>("");
  const [tagInputValue, setTagInputValue] = useState<string>("");
  const [isSaveChangesClicked, setIsSaveChangesClicked] =
    useState<boolean>(false);

  const { isLoading } = useSelector((state: any) => state.Blog);
  const navigate = useNavigate();

  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      slug: blogInfo.slug || "",
      title: blogInfo.title || "",
      coverImage: blogInfo?.coverImage || "",
      categories: blogInfo.categories || [],
      tags: blogInfo.tags || [],
    },
    validationSchema: Yup.object({
      slug: Yup.string().required("Please Enter sku").max(60),
      title: Yup.string().required("Blog title is required").max(60),
      // coverImage: Yup.object()
      // .shape({
      //   objectUrl: Yup.string().required("Image URL is required"),
      //   name: Yup.string().required("File name is required"),
      // })
      // .required("Cover image is required"), // ✅ Now it expects an object
      coverImage: Yup.string().required("Cover image is required"),
      categories: Yup.array().min(1, "At least one category is required"),
      tags: Yup.array().min(1, "At least one tag is required"),
    }),

    onSubmit: async (values) => {
      setBlogInfo((prev) => ({
        ...prev,
        title: values.title,
        slug: values.slug,
        coverImage: values.coverImage,
        categories: values.categories,
        tags: values.tags,
      }));
      blogValidation.setFieldValue("title", values.title);
      blogValidation.setFieldValue("slug", values.slug);
      blogValidation.setFieldValue("coverImage", values.coverImage);
      blogValidation.setFieldValue("categories", values.categories);
      blogValidation.setFieldValue("tags", values.tags);
      if (isBlogEditing && isSaveChangesClicked) {
        await handlePublishBlog();
      } else {
        handleNext();
      }
    },
  });

  const handleAcceptedCoverImage = (files: any) => {
    if (!files.length) return;
    const file = files[0];

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = () => {
      const aspectRatio = img.width / img.height;

      // Allow any size, but enforce a 3:2 aspect ratio
      if (Math.abs(aspectRatio - 3 / 2) < 0.01) {
        const enhancedFile = Object.assign(file, {
          preview: objectUrl,
          file: new Blob([file], { type: file.type }),
          blobName: file.name,
        });

        setBlogMedia((prev: any) => ({
          ...prev,
          coverImage: enhancedFile, // Store the updated file object inside coverImage
        }));

        setSelectedCoverImage({ preview: objectUrl, file });
        validation.setFieldValue("coverImage", objectUrl);

        // validation.setFieldValue("coverImage", objectUrl);
        // validation.setFieldValue("coverImage", {name: file.name});

        // Store the file name in blogInfo
        validation.setFieldTouched("coverImage", true, false);
        setBlogInfo((prev) => ({
          ...prev,
          coverImageName: file.name, // Store the file name separately
        }));
      } else {
        // Set validation error if aspect ratio does not match
        validation.setFieldError(
          "coverImage",
          "Image must have a 3:2 aspect ratio."
        );
        validation.setFieldTouched("coverImage", true, false);
        setSelectedCoverImage({}); // Clear the selected image
        URL.revokeObjectURL(objectUrl); // Clean up the object URL
      }
    };
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  useEffect(() => {
    if (blogInfo.categories) {
      setCategoryInputValue(blogInfo.categories.join(", "));
    }
    if (blogInfo.tags) {
      setTagInputValue(blogInfo.tags.join(", "));
    }
  }, [blogInfo.categories, blogInfo.tags]);

  // const coverImageData = validation.values.coverImage
  // ? JSON.parse(validation.values.coverImage)
  // : null;

  return (
    <div>
      <form onSubmit={validation.handleSubmit}>
        <div>
          <div className="pt-5">
            <label
              htmlFor="productNameInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Blog Title
            </label>
            <input
              type="text"
              id="productNameInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Blog title"
              name="blogTitle"
              value={validation.values.title}
              onChange={(e) => {
                validation.setFieldValue("title", e.target.value);
                const skuValue = e.target.value
                  .toLowerCase()
                  .trim()
                  .replace(/\s+/g, "-");
                validation.setFieldValue("slug", skuValue);
              }}
            />
            {validation.errors.title && validation.touched.title ? (
              <p className="mt-1 text-sm text-red-400">
                {validation.errors.title}
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-400 dark:text-zink-200">
                Do not exceed 60 characters when entering the blog title.
              </p>
            )}
          </div>
          <div className="pt-5">
            <label
              htmlFor="blogSku"
              className="inline-block mb-2 text-base font-medium"
            >
              SKU
            </label>
            <input
              type="text"
              id="blogSku"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="SKU"
              name="slug"
              value={validation.values.slug}
              onChange={validation.handleChange}
              disabled
            />
            {validation.errors.slug && validation.touched.slug && (
              <p className="mt-1 text-sm text-red-400">
                {validation.errors.slug}
              </p>
            )}
          </div>
          <div className="pt-5">
            <label
              htmlFor="coverImage"
              className="inline-block mb-2 text-base font-medium"
            >
              Cover Image
            </label>
            <Dropzone
              accept={{
                "image/jpeg": [".jpg", ".jpeg"],
                "image/png": [".png"],
                "image/webp": [".webp"],
              }}
              onDrop={(acceptedFiles: any) => {
                handleAcceptedCoverImage(acceptedFiles);
              }}
            >
              {({ getRootProps, getInputProps }: any) => (
                <div
                  {...getRootProps()} // Makes the preview clickable and enables drag-and-drop
                  className="flex w-full items-center justify-center bg-white border border-dashed rounded-md cursor-pointer dropzone border-slate-300 dropzone2 dark:bg-zink-700 dark:border-zink-500"
                >
                  <input {...getInputProps()} /> {/* File input field */}
                  {validation.values.coverImage ? (
                    <>
                      <div className="rounded border-slate-200 dark:border-zink-500">
                        <div className="p-2 text-center">
                          <div>
                            <div className="p-2 mx-auto rounded-md h-[200px] bg-slate-100 dark:bg-zink-600">
                              <img
                                data-dz-thumbnail
                                className="block w-full h-full rounded-md"
                                src={validation.values.coverImage}
                                alt={validation.values.coverImage.name}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="w-full py-5 text-lg text-center dz-message needsclick">
                      <div className="mb-3">
                        <UploadCloud className="block size-12 mx-auto text-slate-500 fill-slate-200 dark:text-zink-200 dark:fill-zink-500"></UploadCloud>
                      </div>
                      <h5 className="mb-0 font-normal text-slate-500 text-15">
                        Drag and drop your files or <a href="#!">browse</a> your
                        files
                      </h5>
                    </div>
                  )}
                </div>
              )}
            </Dropzone>
            {validation.errors.coverImage && validation.touched.coverImage && (
              <p className="mt-1 text-sm text-red-400">
                {validation.errors.coverImage}
              </p>
            )}
          </div>
        </div>
        <div className="pt-5 grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-2">
          <div className="xl:cols-span-6">
            <label
              htmlFor="category"
              className="inline-block mb-2 text-base font-medium"
            >
              Categories
            </label>
            <input
              type="text"
              id="category"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Category1, Category2 ..."
              name="categories"
              value={categoryInputValue} // Convert array to string for input display
              onChange={(e) => {
                let value = e.target.value;
                value = value.replace(/[^a-zA-Z0-9, ]/g, "");
                const categoriesArray = value
                  .split(",")
                  .map((item) => item.trim())
                  .filter((item) => item); // Split by comma and trim spaces
                validation.setFieldValue("categories", categoriesArray);
                setCategoryInputValue(value);
              }}
            />
            {validation.errors.categories && validation.touched.categories && (
              <p className="mt-1 text-sm text-red-400">
                {validation.errors.categories}
              </p>
            )}
          </div>
          <div className="xl:cols-span-6">
            <label
              htmlFor="productNameInput"
              className="inline-block mb-2 text-base font-medium"
            >
              Tags
            </label>
            <input
              type="text"
              id="productNameInput"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Tag1, Tag2 ..."
              name="tags"
              value={tagInputValue}
              onChange={(e) => {
                let value = e.target.value;
                value = value.replace(/[^a-zA-Z0-9, ]/g, "");
                const tagsArray = value
                  .split(",")
                  .map((tag) => tag.trim())
                  .filter((tag) => tag);
                validation.setFieldValue("tags", tagsArray);
                setTagInputValue(value);
              }}
            />
            {validation.errors.tags && validation.touched.tags && (
              <p className="mt-1 text-sm text-red-400">
                {validation.errors.tags}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-x-3 pt-10">
          <button
            type="button"
            className={`${
              isLoading ? "cursor-not-allowed opacity-50" : ""
            } text-red-500`}
            onClick={() => {
              blogValidation.resetForm();
              navigate("/blogs");
            }}
            disabled={isLoading}
          >
            <div className="flex align-center">
              <span className="ml-1">Cancel</span>
            </div>
          </button>
          <button
            type="submit"
            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            onClick={handleSubmit}
          >
            Next
          </button>
          {isBlogEditing && (
            <button
              type="button"
              className={`text-white ${
                isLoading
                  ? "opacity-50 cursor-not-allowed pointer-events-none"
                  : ""
              } btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20`}
              // onClick={() => setDeleteSection(true)}
              disabled={isLoading}
              // onClick={(e) => {
              //   e.preventDefault();
              //   handlePublishBlog();
              //   // navigate("/blogs");
              // }}
              onClick={(e) => {
                handleSubmit(e);
                setIsSaveChangesClicked(true);
              }}
            >
              <div className="flex align-center">
                {isLoading && (
                  <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                )}
                <span className="ml-1">Publish Blog</span>
              </div>
            </button>
            // <button
            //   type="button"
            //   className={`text-white ${
            //     isLoading
            //       ? "opacity-50 cursor-not-allowed pointer-events-none"
            //       : ""
            //   } btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20`}
            //   disabled={isLoading}
            //   onClick={(e) => {
            //     e.preventDefault();
            //     handlePublishBlog();
            //     // handleUpdateStatus();
            //     // navigate("/blogs");
            //   }}
            // >
            //   <div className="flex align-center">
            //     {isLoading && (
            //       <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
            //     )}
            //     <span className="ml-1">Publish Blog</span>
            //   </div>
            // </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default BlogInfoForm;
